import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import moment from 'moment-timezone'
import { toast } from 'react-toastify'

import * as S from './styles'
import {
  cpfMask,
  dateMask,
  formatMask,
  phoneMask
} from 'presentation/utils/masks'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import { ContainerNew } from 'presentation/shared/components/ContainerNew'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import SelectFieldNew from 'presentation/shared/components/SelectFieldNew'
import SheetModalNew from 'presentation/shared/components/SheetModalNew'
import Divider from 'presentation/shared/components/DividerNew'
import { ReactComponent as CloseIcon } from 'presentation/assets/icons/close.svg'
import capitalizeFirstLetters from 'common/utils/capitalizeFirstLetters'
import { useStores } from 'presentation/hooks/use-stores'
import { useServices } from 'presentation/hooks/use-services'
import { useHistory, useLocation } from 'react-router'
import { Patient } from 'domain/entities/patient-model'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import { GetAllHealthPlans } from 'domain/usecases/patient/get-all-health-plans'
import { validationSchema } from './validationSchema'
import { NewHealthInsuranceProps } from 'presentation/contexts/surgical-order/surgical-order-context'

const ReviewPatientForm = WithLoading(({ setIsLoading }: WithLoadingProps) => {
  const { state } = useLocation<{
    isEditing?: boolean
  }>()
  const HOSPITAL_SALVADOR_ID = 8
  const HOSPITAL_EMEC_ID = 22
  const HEALTH_PLAN_CODE_PARTICULAR = 304
  const surgicalOrderContext = useStores().surgicalOrder
  const patientService = useServices().patient
  const surgicalOrderService = useServices().surgicalOrder
  const history = useHistory()
  const [healthInsurances, setHealthInsurances] =
    useState<GetAllHealthPlans.Model>([])
  const [insuranceOptions, setInsuranceOptions] = useState<any>([])
  const [healthPlanOptions, setHealthPlanOptions] = useState<any>([])
  const [healthSubPlanOptions, setHealthSubPlanOptions] = useState<any>([])
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [patientToCompare, setPatientToCompare] = useState<Patient>(
    {} as Patient
  )

  const formik = useFormik({
    initialValues: {
      healthInsurance:
        surgicalOrderContext.newHealthInsurance ||
        surgicalOrderContext.lastUsedHealthInsurance
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: async (values) => {
      if (surgicalOrderContext.patient.patient?.user && patientToCompare.user) {
        try {
          surgicalOrderContext.setNewHealthInsurance(values.healthInsurance)
          setIsLoading(true)
          await updatePatient()
          await updateSurgicalOrder()
          await createSurgicalOrder()

          if (state?.isEditing) {
            history.push('/novo-pedido/resumo')
          } else if (
            surgicalOrderContext.location.hospital?.hospital_id ===
              HOSPITAL_SALVADOR_ID ||
            surgicalOrderContext.location.hospital?.hospital_id ===
              HOSPITAL_EMEC_ID
          ) {
            history.push('/novo-pedido/cooperativa')
          } else {
            history.push('/novo-pedido/procedimentos')
          }
        } catch (err: any) {
          toast.error(`[Dados do paciente] ${err.message}`)
          return history.goBack()
        } finally {
          setIsLoading(false)
        }
      }
    }
  })

  const handleEmailChange = (email: string) => {
    if (patientToCompare.user) {
      setPatientToCompare({
        ...patientToCompare,
        user: {
          ...patientToCompare.user,
          email
        }
      })
    }
  }

  const handlePhoneChange = (phone: string) => {
    if (patientToCompare.user) {
      setPatientToCompare({
        ...patientToCompare,
        user: {
          ...patientToCompare.user,
          phone
        }
      })
    }
  }

  const isAnyRequiredFieldEmpty = (): boolean => {
    return (
      (!patientToCompare?.name && !patientToCompare?.patient_name) ||
      !patientToCompare?.user?.email ||
      !patientToCompare?.user?.phone
    )
  }

  const isFieldUpdated = (): boolean => {
    return (
      surgicalOrderContext.patient.patient?.user?.email !==
        patientToCompare?.user?.email ||
      surgicalOrderContext.patient.patient?.user?.phone !==
        patientToCompare?.user?.phone
    )
  }

  const updatePatient = async () => {
    if (!isFieldUpdated()) return
    const phone = patientToCompare.user?.phone
    const formattedPhone = phone ? phone?.replace(/\D/g, '') : phone

    await patientService.updatePatient({
      patient_id: patientToCompare.patient_id!,
      email: patientToCompare.user?.email,
      phone: formattedPhone
    })

    const searchPatientList = surgicalOrderContext.patient.searchPatientList
    const currentPatientIndex =
      surgicalOrderContext.patient.searchPatientList?.findIndex(
        (patient) => patient?.patient_id === patientToCompare.patient_id
      )

    if (Number(currentPatientIndex) !== -1) {
      searchPatientList[Number(currentPatientIndex)] = patientToCompare
    }

    surgicalOrderContext.patient = {
      ...surgicalOrderContext.patient,
      patient: patientToCompare,
      searchPatientList: searchPatientList
    }

    // toast.success('As informações do paciente foram atualizadas!')
  }

  const createSurgicalOrder = async () => {
    if (
      state?.isEditing ||
      surgicalOrderContext.surgicalOrder.surgical_order_id
    )
      return

    const surgicalOrderResponse =
      await surgicalOrderService.createSurgicalOrder({
        hospital_id: surgicalOrderContext.location.hospital?.hospital_id,
        patient: {
          patient_id: surgicalOrderContext.patient.patient?.patient_id,
          patient_name:
            surgicalOrderContext.patient.patient?.name ||
            surgicalOrderContext.patient.patient?.patient_name
        },
        surgical_center: {
          surgical_center_id:
            surgicalOrderContext.location?.surgeryCenter?.surgery_center_id ||
            surgicalOrderContext.location?.surgeryCenter?.surgical_center_id,
          surgical_center:
            surgicalOrderContext.location?.surgeryCenter?.name ||
            surgicalOrderContext.location?.surgeryCenter?.description
        },
        health_insurance: {
          health_insurance_code: Number(
            surgicalOrderContext.getNewHealthInsurance()?.health_insurance_code
          ),
          health_insurance_name:
            surgicalOrderContext.getNewHealthInsurance()?.health_insurance_name,
          health_insurance_card:
            surgicalOrderContext.getNewHealthInsurance()?.health_card,
          health_plan_code: Number(
            surgicalOrderContext.getNewHealthInsurance()?.health_plan_code
          ),
          health_plan_name:
            surgicalOrderContext.getNewHealthInsurance()?.health_plan_name,
          health_sub_plan_code: Number(
            surgicalOrderContext.getNewHealthInsurance()?.health_sub_plan_code
          ),
          health_sub_plan_name:
            surgicalOrderContext.getNewHealthInsurance()?.health_sub_plan_name,
          company_name:
            surgicalOrderContext.getNewHealthInsurance()?.company_name,
          valid_thru: surgicalOrderContext.getNewHealthInsurance()?.valid_thru
        }
      })
    surgicalOrderContext.setSurgicalOrder(surgicalOrderResponse)
  }

  const updateSurgicalOrder = async () => {
    if (
      // !state?.isEditing ||
      !surgicalOrderContext.surgicalOrder?.surgical_order_id
    )
      return

    await surgicalOrderService.updateSurgicalOrder({
      surgical_order_id:
        surgicalOrderContext.getSurgicalOrder()?.surgical_order_id,
      patient: {
        patient_id: surgicalOrderContext.getPatient().patient?.patient_id,
        patient_name:
          surgicalOrderContext.getPatient().patient?.name ||
          surgicalOrderContext.getPatient().patient?.patient_name
      },
      health_insurance: {
        health_insurance_id:
          surgicalOrderContext.getNewHealthInsurance()?.health_insurance_id,
        health_insurance_code: Number(
          surgicalOrderContext.getNewHealthInsurance()?.health_insurance_code
        ),
        health_insurance_name:
          surgicalOrderContext.getNewHealthInsurance()?.health_insurance_name,
        health_insurance_card:
          surgicalOrderContext.getNewHealthInsurance()?.health_card,
        health_plan_code: Number(
          surgicalOrderContext.getNewHealthInsurance()?.health_plan_code
        ),
        health_plan_name:
          surgicalOrderContext.getNewHealthInsurance()?.health_plan_name,
        health_sub_plan_code: Number(
          surgicalOrderContext.getNewHealthInsurance()?.health_sub_plan_code
        ),
        health_sub_plan_name:
          surgicalOrderContext.getNewHealthInsurance()?.health_sub_plan_name,
        company_name:
          surgicalOrderContext.getNewHealthInsurance()?.company_name,
        valid_thru: surgicalOrderContext.getNewHealthInsurance()?.valid_thru
      }
    })

    // toast.success('Os dados do pedido foram atualizados!')
  }

  const getHealthInsuranceData = async () => {
    setIsLoading(true)
    try {
      await loadHealthPlans()
      await loadPatientHealthInsuranceInfo()
    } catch (error: any) {
      console.error(error?.message)
    } finally {
      setIsLoading(false)
    }
  }

  async function loadHealthPlans() {
    try {
      const response = await patientService.getAllHealthPlans()

      if (response.length > 0) {
        setHealthInsurances(response)

        const mappedHealthInsuranceOptions = response?.map(
          (healthInsurance) => {
            return {
              value: healthInsurance.health_insurance_id,
              label: healthInsurance.name
            }
          }
        )

        setInsuranceOptions(mappedHealthInsuranceOptions)
      }
    } catch (error: any) {
      toast.error(error?.message)
    }
  }

  /**
   * TODO:
   * Verificar se existe convênio selecionado no context
   * Se existir, carregar os dados do convênio no formulário
   * Se não existir, buscar o último convênio utilizado pelo paciente.
   * Atualmente, a função sempre busca o último convênio utilizado.
   */
  async function loadPatientHealthInsuranceInfo() {
    setIsLoading(true)
    try {
      const response = await patientService.getHealthInsuranceByPatient({
        is_active: true,
        last_health_insurance: true,
        patient_id: surgicalOrderContext?.patient?.patient?.patient_id
      })

      if (response?.length > 0) {
        const lastUsedHealthInsurance = {
          ...response[0],
          valid_thru:
            response[0].valid_thru &&
            moment.utc(response[0].valid_thru).isValid()
              ? moment.utc(response[0].valid_thru).format('DD/MM/YYYY')
              : ''
        }

        surgicalOrderContext.setLastUsedHealthInsurance(lastUsedHealthInsurance)
        formik.setFieldValue('healthInsurance', lastUsedHealthInsurance)
      } else {
        formik.setFieldValue('healthInsurance', {} as NewHealthInsuranceProps)
        surgicalOrderContext.setLastUsedHealthInsurance(
          {} as NewHealthInsuranceProps
        )
      }

      return response?.[0]
    } catch (error: any) {
      toast.error(error?.message)
    } finally {
      setIsLoading(false)
    }
  }

  async function loadHealthSubPlans(
    healthInsuranceId: number,
    healthPlanId: number
  ) {
    try {
      const response = await patientService.getHealthSubPlans({
        health_insurance_id: healthInsuranceId,
        plan_id: healthPlanId
      })

      if (response.length > 0) {
        const mappedHealthSubPlansOptions = response.map((subPlan: any) => {
          return {
            label: subPlan.name,
            value: subPlan.sub_plan_id
          }
        })
        setHealthSubPlanOptions(mappedHealthSubPlansOptions)
      }
    } catch (error: any) {
      toast.error(error?.message)
    }
  }

  const getSubPlanData = async () => {
    setIsLoading(true)
    try {
      if (
        !!formik.values.healthInsurance?.health_insurance_code &&
        !!formik.values.healthInsurance?.health_plan_code
      ) {
        await loadHealthSubPlans(
          Number(formik.values.healthInsurance?.health_insurance_code),
          Number(formik.values.healthInsurance?.health_plan_code)
        )
      }
    } catch (error: any) {
      console.error(error?.message)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (
      !surgicalOrderContext.location.hospital?.hospital_id ||
      !surgicalOrderContext.patient.patient?.patient_id
    ) {
      return history.push('/novo-pedido/local')
    }
    getHealthInsuranceData()
    setPatientToCompare({
      ...surgicalOrderContext.patient.patient
    })
  }, [])

  useEffect(() => {
    if (
      !!formik.values.healthInsurance?.health_insurance_code &&
      !!formik.values.healthInsurance?.health_plan_code
    ) {
      setTimeout(() => {
        getSubPlanData()
      }, 800)
    }
  }, [
    formik.values.healthInsurance?.health_insurance_code,
    formik.values.healthInsurance?.health_plan_code
  ])

  useEffect(() => {
    if (
      healthInsurances.length > 0 &&
      !!formik?.values.healthInsurance?.health_insurance_code
    ) {
      setIsLoading(true)

      const filteredHealthPlanOptions = healthInsurances
        ?.filter(
          (healthInsurance) =>
            healthInsurance.health_insurance_id ===
            formik?.values.healthInsurance?.health_insurance_code
        )
        ?.map((healthInsurance) => healthInsurance.plans)

      const mappedHealthPlanOptions = filteredHealthPlanOptions[0]?.map(
        (item) => {
          return {
            label: item.name,
            value: item.plan_id
          }
        }
      )

      setHealthPlanOptions(mappedHealthPlanOptions)
      if (mappedHealthPlanOptions?.length > 0) {
        setIsLoading(false)
      }
    }
  }, [formik.values.healthInsurance?.health_insurance_code, insuranceOptions])

  function handleChangeHealthInsurance(event: any) {
    const selectedOption = insuranceOptions.find(
      (option: any) => String(option.value) === event.target.value
    )

    if (selectedOption) {
      formik.setFieldValue(
        'healthInsurance.health_insurance_code',
        selectedOption.value
      )
      formik.setFieldValue(
        'healthInsurance.health_insurance_name',
        selectedOption.label
      )
    }

    formik.setFieldValue('healthInsurance.health_plan_code', '')
    formik.setFieldValue('healthInsurance.health_plan_name', '')
    formik.setFieldValue('healthInsurance.health_sub_plan_code', '')
    formik.setFieldValue('healthInsurance.health_sub_plan_name', '')
    formik.setFieldValue('healthInsurance.health_card', '')
    formik.setFieldValue('healthInsurance.valid_thru', '')
    formik.setFieldValue('healthInsurance.company_name', '')
    setHealthSubPlanOptions([])
  }

  function handleChangePlanCode(event: any) {
    const selectedOption = healthPlanOptions.find(
      (option: any) => String(option.value) === event.target.value
    )

    if (selectedOption) {
      formik.setFieldValue(
        'healthInsurance.health_plan_code',
        selectedOption.value
      )
      formik.setFieldValue(
        'healthInsurance.health_plan_name',
        selectedOption.label
      )
    }

    formik.handleChange(event)
    formik.setFieldValue('healthInsurance.health_sub_plan_code', '')
    formik.setFieldValue('healthInsurance.health_sub_plan_name', '')
    formik.setFieldValue('healthInsurance.health_card', '')
    formik.setFieldValue('healthInsurance.valid_thru', '')
    formik.setFieldValue('healthInsurance.company_name', '')
    setHealthSubPlanOptions([])
  }

  function handleChangeSubPlanCode(event: any) {
    const selectedOption = healthSubPlanOptions.find(
      (option: any) => String(option.value) === event.target.value
    )

    if (selectedOption) {
      formik.setFieldValue(
        'healthInsurance.health_sub_plan_code',
        selectedOption.value
      )
      formik.setFieldValue(
        'healthInsurance.health_sub_plan_name',
        selectedOption.label
      )
    }

    formik.handleChange(event)
    formik.setFieldValue('healthInsurance.health_card', '')
    formik.setFieldValue('healthInsurance.valid_thru', '')
    formik.setFieldValue('healthInsurance.company_name', '')
  }

  return (
    <>
      <ContainerNew
        title="Dados do paciente"
        subtitle="Confira os dados do paciente e convênio"
        titleColor="primary600"
        subtitleColor="neutral700"
        buttonsHasBoxShadow={!state?.isEditing}
        primaryButton={
          <ButtonNew
            onClick={() => {
              isFieldUpdated()
                ? setIsConfirmationModalOpen(true)
                : formik.submitForm()
            }}
            fullWidth
            size="large"
            disabled={
              isAnyRequiredFieldEmpty() ||
              !formik.isValid ||
              formik.isSubmitting
            }
          >
            {state?.isEditing ? 'Salvar edição' : 'Próximo'}
          </ButtonNew>
        }
        secondaryButton={
          <ButtonNew size="large" onClick={history.goBack} fullWidth outlined>
            Anterior
          </ButtonNew>
        }
      >
        <S.FormWrapper onSubmit={formik.handleSubmit}>
          {surgicalOrderContext.patient.patient?.user?.cpf && (
            <TextFieldNew
              labelOverlapping
              label="CPF do paciente"
              labelSize="xxsmall"
              labelWeight="semiBold"
              color="primary600"
              disabled
              value={
                surgicalOrderContext.patient.patient?.user?.cpf
                  ? formatMask(
                      cpfMask,
                      surgicalOrderContext.patient.patient?.user.cpf
                    )
                  : ''
              }
              id="cpf"
              name="cpf"
              mask={cpfMask}
              data-testid="review-patient-cpf-input"
              required
              requiredColor="neutral800"
              labelColor="neutral800"
            />
          )}
          <TextFieldNew
            labelOverlapping
            label="Nome do paciente"
            labelSize="xxsmall"
            labelWeight="semiBold"
            disabled
            value={
              surgicalOrderContext.patient.patient?.name
                ? capitalizeFirstLetters(
                    surgicalOrderContext.patient.patient?.name
                  )
                : surgicalOrderContext.patient.patient?.patient_name
                ? capitalizeFirstLetters(
                    surgicalOrderContext.patient.patient?.patient_name
                  )
                : ''
            }
            id="name"
            name="name"
            data-testid="review-patient-name-input"
            required
            requiredColor="neutral800"
            labelColor="neutral800"
          />
          <TextFieldNew
            labelOverlapping
            label="E-mail"
            labelSize="xxsmall"
            labelWeight="semiBold"
            value={patientToCompare.user?.email || ''}
            data-testid="review-patient-email-input"
            required
            requiredColor="neutral800"
            labelColor="neutral800"
            id="email"
            name="email"
            onChange={formik.handleChange}
            onInputChange={handleEmailChange}
          />
          <TextFieldNew
            labelOverlapping
            label="Celular"
            labelSize="xxsmall"
            labelWeight="semiBold"
            value={
              patientToCompare.user?.phone
                ? formatMask(
                    phoneMask,
                    patientToCompare.user?.phone
                      ?.replace(/\D/g, '')
                      .replace(/^55/, '')
                  )
                : ''
            }
            data-testid="review-patient-phone-input"
            required
            requiredColor="neutral800"
            labelColor="neutral800"
            id="phone"
            name="phone"
            onInputChange={handlePhoneChange}
            mask={phoneMask}
          />
          <SelectFieldNew
            labelOverlapping
            label="Convênio *"
            labelSize="xxsmall"
            labelWeight="semiBold"
            labelColor="neutral700"
            placeholder="Selecione"
            name="healthInsurance.health_insurance_code"
            items={insuranceOptions}
            onChange={handleChangeHealthInsurance}
            onBlur={formik.handleBlur}
            error={
              formik.touched.healthInsurance?.health_insurance_code
                ? formik.errors.healthInsurance?.health_insurance_code
                : undefined
            }
            value={formik.values.healthInsurance?.health_insurance_code || ''}
          />

          {formik.values.healthInsurance?.health_insurance_code !==
            HEALTH_PLAN_CODE_PARTICULAR && (
            <>
              <SelectFieldNew
                label="Plano"
                labelOverlapping
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="neutral700"
                placeholder="Selecione"
                name="healthInsurance.health_plan_code"
                value={formik.values.healthInsurance?.health_plan_code || ''}
                onChange={handleChangePlanCode}
                items={healthPlanOptions}
              />
              <SelectFieldNew
                label="Subplano"
                labelOverlapping
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="neutral700"
                placeholder="Selecione"
                name="healthInsurance.sub_plan_code"
                value={
                  formik.values.healthInsurance?.health_sub_plan_code || ''
                }
                onChange={handleChangeSubPlanCode}
                items={healthSubPlanOptions}
              />
              <TextFieldNew
                type="text"
                label="Número da carteira "
                labelOverlapping
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="neutral700"
                placeholder="Digite"
                name="healthInsurance.health_card"
                value={formik.values.healthInsurance?.health_card}
                onChange={formik.handleChange('healthInsurance.health_card')}
                onBlur={formik.handleBlur}
                required={
                  formik.values.healthInsurance?.health_insurance_code !==
                  HEALTH_PLAN_CODE_PARTICULAR
                }
                requiredColor="neutral800"
                error={
                  formik.touched.healthInsurance?.health_card &&
                  formik.errors.healthInsurance?.health_card
                }
                maxLength={25}
              />
              <TextFieldNew
                label="Empresa (titular do plano)"
                labelOverlapping
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="neutral700"
                placeholder="Digite"
                name="healthInsurance.company_name"
                value={formik.values.healthInsurance?.company_name}
                onChange={formik.handleChange('healthInsurance.company_name')}
                onBlur={formik.handleBlur}
              />
              <TextFieldNew
                label="Validade da carteira"
                placeholder="dd/mm/aaaa"
                labelOverlapping
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="neutral700"
                name="healthInsurance.valid_thru"
                mask={dateMask}
                onChange={formik.handleChange('healthInsurance.valid_thru')}
                onBlur={formik.handleBlur}
                value={formik.values.healthInsurance?.valid_thru}
                error={
                  formik.touched.healthInsurance?.valid_thru &&
                  formik.errors.healthInsurance?.valid_thru
                }
              />
            </>
          )}
        </S.FormWrapper>
        <SheetModalNew
          isOpen={isConfirmationModalOpen}
          onClose={() => setIsConfirmationModalOpen(false)}
        >
          <S.ModalWrapper>
            <S.Title>
              Atualizar informações
              <CloseIcon onClick={() => setIsConfirmationModalOpen(false)} />
            </S.Title>
            <Divider color="neutral100" />
            <S.Complement>
              <S.ComplementBold>
                Você tem certeza que deseja atualizar as informações do
                paciente?
              </S.ComplementBold>
              <br />
              Esta ação não poderá ser desfeita.
            </S.Complement>
            <S.ModalButtons>
              <ButtonNew
                onClick={() => {
                  setIsConfirmationModalOpen(false)
                  formik.submitForm()
                }}
                fullWidth
              >
                Atualizar informações
              </ButtonNew>
              <ButtonNew
                onClick={() => setIsConfirmationModalOpen(false)}
                fullWidth
                variant="text"
              >
                Cancelar
              </ButtonNew>
            </S.ModalButtons>
          </S.ModalWrapper>
        </SheetModalNew>
      </ContainerNew>
    </>
  )
})

export default ReviewPatientForm
